import { useState, useMemo, useCallback, useContext } from 'react'
import useSettings from '../../hooks/Settings'
import AnalysisEffectCard from '../analysis/AnalysisEffectCard'
import DeepAnalysisContextCard from './DeepAnalysisContextCard'
import DeepAnalysisParticipationSection from './DeepAnalysisParticipationSection'
import DeepAnalysisResultSection from './DeepAnalysisResultSection'
import DeepAnalysisPassthroughFrame from './DeepAnalysisPassthroughFrame'
import DeepAnalysisAISection from './DeepAnalysisAISection'
import EffectScatterSection from './EffectScatterSection'
import CorrelationMatrixSection from './CorrelationMatrixSection'
import EffectPairSection from './EffectPairSection'
import DeepAnalysisSlotsContext from '../../StateManagment/DeepAnalysisSlotsContext'
import DeepAnalysisAIContext from '../../StateManagment/DeepAnalysisAIContext'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'

/**
 * Section stack of the effect deep-analysis page (redesign order):
 * context card → resultaat → spreiding → correlatiematrix (Deccos Pro,
 * MSI effects) → correlatie-paar → deelname → AI.
 *
 * Owns the portal targets the indicator data layer (QuestionData) fills via
 * DeepAnalysisSlotsContext: the facts pane of the context card and the body
 * of the "Deelname" section. Data fetching stays in the data layer; only the
 * place where it renders changes.
 *
 * @param {object} props
 * @param {object} props.effect - The anchor OutputEffects doc.
 * @param {Array<object>} [props.personas] - Personas list (loaded at page level).
 * @param {boolean} [props.personasEnabled] - Whether the persona filter should render.
 */
const DeepAnalysisEffectSections = ({ effect, personas, personasEnabled }) => {
    const { deccosPro } = useSettings()
    const [factsEl, setFactsEl] = useState(null)
    const [participationEl, setParticipationEl] = useState(null)
    // Second set of panes for the compare period (see slotsForSide).
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const [factsElB, setFactsElB] = useState(null)
    const [participationElB, setParticipationElB] = useState(null)

    // Summaries the sections publish for Decca (spreiding, correlaties, …).
    // Equality guard so a section republishing the same summary doesn't loop.
    const [pageContext, setPageContext] = useState(/** @type {Object<string, any>} */ ({}))
    const publish = useCallback((key, value) => {
        setPageContext((prev) => {
            if (JSON.stringify(prev[key]) === JSON.stringify(value)) return prev
            return { ...prev, [key]: value }
        })
    }, [])
    const aiContext = useMemo(() => ({ publish, pageContext }), [publish, pageContext])

    // Switching anchors (matrix cell click to another effect) keeps this
    // component mounted — drop the previous effect's summaries so Decca
    // never receives effect A's numbers as effect B's context. Reset during
    // render (not in an effect): child publish effects run before a parent
    // effect would, so an effect-based reset would wipe the new anchor's
    // fresh publishes.
    // AI-CONTEXT: See docs/pitfalls.md#state-stale-derived-state-on-item-cycler-prop-change
    // Same for a period change: the summaries describe the previous period.
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const period = useContext(DeepAnalysisPeriodContext)
    const contextKey = JSON.stringify([effect.ID, period?.rangeA, period?.rangeB, Boolean(period?.compareActive)])
    const [prevContextKey, setPrevContextKey] = useState(contextKey)
    if (prevContextKey !== contextKey) {
        setPrevContextKey(contextKey)
        setPageContext({})
    }

    // Correlatiematrix: researcher tier (Deccos Pro) + only for MSI effects —
    // the matrix spans the tenant's Standard effects, so a non-MSI anchor
    // wouldn't appear in its own matrix.
    const showMatrix = deccosPro && effect.Standard === 'true'

    const slots = useMemo(
        () => ({ factsEl, participationEl, B: { factsEl: factsElB, participationEl: participationElB } }),
        [factsEl, participationEl, factsElB, participationElB]
    )

    return (
        <DeepAnalysisSlotsContext.Provider value={slots}>
        {/* Page-context feeding to Decca is researcher-tier: without Pro the
            provider stays null, so sections don't publish and the AI section
            sends no pageContext. */}
        <DeepAnalysisAIContext.Provider value={deccosPro ? aiContext : null}>
            <DeepAnalysisContextCard
                aboutTitle='Over dit effect'
                aboutText={effect.Description}
                setFactsEl={setFactsEl}
                setFactsElB={setFactsElB}
            />
            <DeepAnalysisResultSection>
                <AnalysisEffectCard
                    effect={effect}
                    personas={personas}
                    personasEnabled={personasEnabled}
                    frameless
                    frameComponent={DeepAnalysisPassthroughFrame}
                />
            </DeepAnalysisResultSection>
            <EffectScatterSection effect={effect} />
            {showMatrix && <CorrelationMatrixSection effect={effect} />}
            <EffectPairSection effect={effect} />
            <DeepAnalysisParticipationSection
                setParticipationEl={setParticipationEl}
                setParticipationElB={setParticipationElB}
            />
            <DeepAnalysisAISection type='effect' anchor={effect} />
        </DeepAnalysisAIContext.Provider>
        </DeepAnalysisSlotsContext.Provider>
    )
}

export default DeepAnalysisEffectSections
